import { Button } from "@amazing-care/ui/button";
import { useEffect } from "react";
import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { canReloadForNewDeploy, isStaleChunkError, reloadForNewDeploy } from "@/lib/stale-deploy";
import { ROUTES } from "@/routes";

/**
 * Catches both thrown 404 responses (the replacement for Next's `notFound()`)
 * and unexpected render errors inside a route.
 */
export function RouteError() {
  const error = useRouteError();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;
  // A page whose code is gone after a deploy: reload onto the new build
  // rather than showing an error the user can do nothing about.
  const willReload = isStaleChunkError(error) && canReloadForNewDeploy();

  useDocumentTitle(isNotFound ? "Not found" : "Something went wrong");

  useEffect(() => {
    if (willReload) reloadForNewDeploy();
  }, [willReload]);

  if (willReload) return null;

  const message = isNotFound
    ? "We can't find what you're looking for."
    : error instanceof Error
      ? error.message
      : "An unexpected error occurred.";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-2xl font-semibold">
        {isNotFound ? "Not found" : "Something went wrong"}
      </h1>
      <p className="max-w-md text-sm text-muted-foreground">{message}</p>
      <Button nativeButton={false} render={<Link to={ROUTES.staff} />}>
        Back to the portal
      </Button>
    </div>
  );
}
